import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { ActiveSkill } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Sparkles, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface Props {
  sessionId: string;
  /** 스트리밍 중이면 chip 변경을 비활성화 (turn 진행 중 race 회피). */
  pending?: boolean;
}

/** 동시 활성 최대치. 서버 UpdateSessionSkillsRequestSchema.max(4) 와 동기화. */
const MAX_PINNED = 4;

/**
 * 채팅 상단에 현재 세션의 활성 skill 들을 chip 으로 노출.
 * - X 버튼: 해당 skill 제거
 * - "+" 버튼: 사용자 권한 내 picker 드롭다운 → 선택 시 추가 (4개 cap)
 * SSE done 이후 부모가 invalidate 하므로 turn 마다 자동 재조회.
 */
export function ActiveSkillChips({ sessionId, pending }: Props) {
  const qc = useQueryClient();
  const queryKey = ['chat-active-skills', sessionId];

  const activeQuery = useQuery({
    queryKey,
    queryFn: () => api.chatGetActiveSkills(sessionId),
  });

  const updateMut = useMutation({
    mutationFn: (skillIds: string[]) => api.chatUpdateActiveSkills(sessionId, { skillIds }),
    onSuccess: (next) => {
      // 서버 응답에 available 이 빠질 수 있어 기존 캐시의 available 보존.
      qc.setQueryData(queryKey, (prev: typeof next | undefined) => ({
        skills: next.skills,
        available: next.available ?? prev?.available,
      }));
    },
  });

  const skills: ActiveSkill[] = activeQuery.data?.skills ?? [];
  const available: ActiveSkill[] = activeQuery.data?.available ?? [];

  const [pickerOpen, setPickerOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const pickerRef = useRef<HTMLDivElement | null>(null);

  // 외부 클릭 시 picker 닫기
  useEffect(() => {
    if (!pickerOpen) return;
    const onClick = (e: MouseEvent) => {
      if (!pickerRef.current) return;
      if (!pickerRef.current.contains(e.target as Node)) setPickerOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [pickerOpen]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return available;
    return available.filter(
      (s) => s.name.toLowerCase().includes(q) || s.slug.toLowerCase().includes(q),
    );
  }, [available, filter]);

  // active skill 이 한 개도 없으면 chip UI 자체를 숨김 — 새 세션 첫 메시지 전 / 사용자가 모두 제거한
  // 상태에서 빈 헤더 + "+" 버튼만 뜨는 noise 제거. 첫 메시지 보내면 classifier 가 자동 pin 하므로 곧
  // 다시 등장.
  if (skills.length === 0) return null;

  const disabled = pending || updateMut.isPending;
  const canAdd = skills.length < MAX_PINNED && available.length > 0;

  const onRemove = (id: string) => {
    if (disabled) return;
    const next = skills.filter((s) => s.id !== id).map((s) => s.id);
    updateMut.mutate(next);
  };

  const onAdd = (id: string) => {
    if (disabled) return;
    if (skills.some((s) => s.id === id)) return;
    if (skills.length >= MAX_PINNED) return;
    const next = [...skills.map((s) => s.id), id];
    updateMut.mutate(next);
    setPickerOpen(false);
    setFilter('');
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {skills.length > 0 ? (
        <span className="inline-flex items-center gap-1 text-xs text-foreground-muted">
          <Sparkles className="size-3.5" />
          활성 역할
        </span>
      ) : null}
      {skills.map((s) => (
        <span
          key={s.id}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md border border-border bg-surface-muted/60 px-2 py-0.5 text-xs',
            disabled && 'opacity-70',
          )}
          title={s.description ?? s.name}
        >
          <span className="truncate max-w-[160px]">{s.name}</span>
          <button
            type="button"
            onClick={() => onRemove(s.id)}
            disabled={disabled}
            aria-label={`${s.name} 제거`}
            className="grid place-items-center rounded-sm text-foreground-muted hover:text-foreground hover:bg-surface-muted disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-foreground-muted"
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
      {canAdd ? (
        <div className="relative" ref={pickerRef}>
          <button
            type="button"
            onClick={() => setPickerOpen((v) => !v)}
            disabled={disabled}
            aria-label="역할 추가"
            className="inline-flex items-center gap-1 rounded-md border border-dashed border-border bg-transparent px-2 py-0.5 text-xs text-foreground-muted hover:border-accent hover:text-foreground disabled:cursor-not-allowed"
          >
            <Plus className="size-3" />
            역할 추가
          </button>
          {pickerOpen ? (
            <div className="absolute bottom-full left-0 mb-1.5 z-10 w-72 rounded-md border border-border bg-surface shadow-lg p-2">
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="이름 검색…"
                className="w-full mb-2 rounded-sm border border-border bg-surface px-2 py-1 text-xs focus-visible:outline-none"
                ref={(el) => {
                  // picker 열릴 때 자동 포커스 — autoFocus prop 대신 ref 로 처리해 a11y 룰 만족.
                  if (el) el.focus();
                }}
              />
              <div className="max-h-60 overflow-y-auto">
                {filtered.length === 0 ? (
                  <div className="px-2 py-1.5 text-xs text-foreground-muted">
                    {available.length === 0 ? '추가할 역할이 없습니다.' : '검색 결과 없음.'}
                  </div>
                ) : (
                  filtered.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => onAdd(s.id)}
                      className="block w-full text-left rounded-sm px-2 py-1.5 text-xs hover:bg-surface-muted"
                    >
                      <div className="font-medium">{s.name}</div>
                      {s.description ? (
                        <div className="truncate text-foreground-muted">{s.description}</div>
                      ) : null}
                    </button>
                  ))
                )}
              </div>
              {skills.length >= MAX_PINNED ? (
                <div className="mt-1 px-2 py-1 text-[11px] text-foreground-muted">
                  최대 {MAX_PINNED}개까지 동시 활성 가능. 먼저 다른 역할을 제거하세요.
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
